import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ArrowLeft, RefreshCw, Square, Waves } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArchiveFilters } from "@/components/archive/ArchiveFilters";
import { ArchivePresets } from "@/components/archive/ArchivePresets";
import { ArchiveEntries } from "@/components/archive/ArchiveEntries";
import { archiveCategory } from "@/lib/archiveCatalog";
import { useArchiveAudio } from "@/hooks/useArchiveAudio";
import "@/components/archive/archive.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Library() {
  const [catalog, setCatalog] = useState({ presets: [], entries: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("name");
  const [headerHeight, setHeaderHeight] = useState(60);
  const audio = useArchiveAudio();

  useEffect(() => {
    const header = document.querySelector('[data-testid="top-header"]');
    if (!header) return;
    const resize = new ResizeObserver(() => setHeaderHeight(header.getBoundingClientRect().height + 8));
    resize.observe(header);
    return () => resize.disconnect();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    Promise.all([
      axios.get(`${API}/frequencies`, { signal: controller.signal }),
      axios.get(`${API}/rife`, { signal: controller.signal }),
    ]).then(([presets, entries]) => {
      setCatalog({ presets: presets.data, entries: entries.data });
    }).catch((err) => {
      if (!axios.isCancel(err)) setError("The archive could not be loaded.");
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [attempt]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return catalog.entries.map((entry, index) => ({ ...entry, id: `entry-${index}` }))
      .filter(entry => (category === "all" || archiveCategory(entry) === category)
        && `${entry.condition} ${entry.frequencies.join(" ")}`.toLowerCase().includes(term))
      .sort((a, b) => sort === "frequency"
        ? Math.min(...a.frequencies) - Math.min(...b.frequencies) || a.condition.localeCompare(b.condition)
        : a.condition.localeCompare(b.condition));
  }, [catalog.entries, query, category, sort]);
  const clearFilters = () => { setQuery(""); setCategory("all"); };

  return (
    <div className="mirrors-archive" data-testid="public-archive">
      <header className="archive-heading">
        <Link to="/" className="archive-back" data-testid="archive-home-link"><ArrowLeft size={14} /> GODMODE SOUND</Link>
        <div className="archive-heading-row">
          <div><p className="archive-eyebrow" data-testid="archive-brand">MIRRORS // 96</p><h1 data-testid="library-title">ARCHIVE</h1></div>
          <div className="archive-access" data-testid="archive-access-status"><span /> PUBLIC ACCESS<br /><small>A = 432 Hz</small></div>
        </div>
      </header>

      <div className="archive-transport" style={{ top: headerHeight }} data-testid="archive-transport">
        <Waves size={20} aria-hidden="true" className={audio.active ? "archive-live" : ""} />
        <div className="archive-transport-label" role="status" aria-live="polite" data-testid="archive-playback-status">
          <strong>{audio.pending ? "STARTING…" : audio.active?.label || "STANDBY"}</strong>
          <span>{audio.active?.detail || "NO ACTIVE TONES"}</span>
        </div>
        <Button className="archive-button" variant="outline" onClick={audio.stop} data-testid="stop-all-library" aria-label="Stop all archive audio"><Square size={14} /> STOP ALL</Button>
      </div>
      {audio.error && <p className="archive-error" role="alert" data-testid="archive-audio-error">{audio.error}</p>}

      {loading ? <div className="archive-state" role="status" data-testid="archive-loading">LOADING ARCHIVE…</div>
        : error ? <div className="archive-state" role="alert" data-testid="archive-load-error">{error}<Button className="archive-button" variant="outline" onClick={() => setAttempt(n => n + 1)} data-testid="archive-retry"><RefreshCw size={14} /> RETRY</Button></div>
        : <div className="archive-layout">
          <ArchivePresets presets={catalog.presets} audio={audio} />
          <section className="archive-catalog" aria-label="Frequency archive" data-testid="archive-catalog">
            <div className="archive-section-heading"><h2 data-testid="archive-section-title">FREQUENCY ARCHIVE</h2><span data-testid="archive-total-count">{catalog.entries.length} SETS</span></div>
            <ArchiveFilters {...{ query, setQuery, category, setCategory, sort, setSort }} />
            <p className="archive-result-count" data-testid="archive-results-count">{filtered.length} / {catalog.entries.length} SETS</p>
            <ArchiveEntries entries={filtered} audio={audio} clearFilters={clearFilters} />
          </section>
        </div>}
    </div>
  );
}